// ShareOrderButton — bouton "Partager la course" (Devis Partagé WhatsApp)
// Demande un token de partage au backend puis ouvre une intent WhatsApp pré-remplie
// (ou Web Share API sur mobile) avec tarif + ETA + lien de suivi public.

import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Share2, Loader2 } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function ShareOrderButton({ order, className = "", "data-testid": testId = "share-order-btn" }) {
  const [loading, setLoading] = useState(false);

  const buildMessage = (publicUrl) => {
    const isTaxi = (order?.order_type || "").toLowerCase() === "taxi";
    const fare = Number(order?.taxi_fare ?? order?.delivery_fee ?? order?.fare ?? 0);
    const km = Number(order?.distance_km ?? order?.taxi_distance_km ?? 0);
    const dest = order?.delivery_address || "destination";
    const header = isTaxi ? "Je viens en taxi WÔMA" : "Ma commande WÔMA arrive";
    const lines = [
      `${header} 🚕`,
      `📍 Destination : ${dest}`,
      km > 0 ? `📏 ${km.toFixed(1)} km` : null,
      fare > 0 ? `💰 Tarif : ${fare.toLocaleString("fr-FR")} FCFA` : null,
      `🔗 Suivi en direct : ${publicUrl}`,
    ].filter(Boolean);
    return lines.join("\n");
  };

  const handleShare = async () => {
    if (!order?.order_id && !order?.id) {
      toast.error("Commande introuvable");
      return;
    }
    const orderId = order.order_id || order.id;
    setLoading(true);
    try {
      const res = await axios.post(`${API}/orders/${orderId}/share-token`);
      const token = res.data?.share_token;
      if (!token) throw new Error("Token manquant");
      const publicUrl = `${window.location.origin}/track/public/${token}`;
      const message = buildMessage(publicUrl);

      // Web Share API (mobile natif) — préférée si dispo
      if (navigator.share) {
        try {
          await navigator.share({
            title: "Suivi de ma course WÔMA",
            text: message,
            url: publicUrl,
          });
          return;
        } catch (e) {
          // L'utilisateur a annulé le partage natif, on bascule sur WhatsApp
          if (e?.name === "AbortError") return;
        }
      }
      // Fallback universel : intent WhatsApp
      const waUrl = `https://wa.me/?text=${encodeURIComponent(message)}`;
      window.open(waUrl, "_blank", "noopener,noreferrer");
    } catch (err) {
      toast.error("Impossible de générer le lien de partage");
    } finally {
      setLoading(false);
    }
  };

  return (
    <button
      onClick={handleShare}
      disabled={loading}
      className={
        `inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-full ` +
        `bg-[#25D366] hover:bg-[#20BA5A] disabled:opacity-50 text-white ` +
        `text-xs font-black uppercase tracking-widest shadow-lg shadow-emerald-500/30 ` +
        `transition-all active:scale-95 ${className}`
      }
      data-testid={testId}
    >
      {loading ? <Loader2 size={14} className="animate-spin" /> : <Share2 size={14} />}
      Partager via WhatsApp
    </button>
  );
}
